import { useForm } from "@inertiajs/react"
import { CheckCircle, ChevronLeft, Star } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { levelUpLeagueTeamPlayerPath } from "@/routes"
import type {
  AdvancementOptions,
  SkillOption,
  StatImprovementOption,
} from "@/types"

import {
  CATEGORY_LABELS,
  getAvailableForRoll,
  labelForRoll,
} from "./advancement-rules"

// ── Types ────────────────────────────────────────────────────────────────────

interface LevelUpPanelProps {
  leagueId: string
  teamId: string
  playerId: string
  playerName: string
  advancementOptions: AdvancementOptions
  advancementsUsed: number
  advancementsEarned: number
}

type Step = "dice" | "select" | "success"

// ── Sub-components ────────────────────────────────────────────────────────────

interface SkillOptionCardProps {
  skill: SkillOption
  selected: boolean
  isPrimary: boolean
  isSecondary: boolean
  onClick: () => void
}

function SkillOptionCard({
  skill,
  selected,
  isPrimary,
  isSecondary,
  onClick,
}: SkillOptionCardProps) {
  const tierLabel = isPrimary ? "Primary" : isSecondary ? "Secondary" : "Any"
  const tierVariant = isPrimary
    ? ("default" as const)
    : isSecondary
      ? ("secondary" as const)
      : ("outline" as const)

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      className="border-border bg-muted hover:border-accent aria-pressed:border-accent flex h-auto w-full cursor-pointer flex-col items-start gap-1.5 border-2 px-3.5 py-3 text-left whitespace-normal transition-all duration-150 hover:bg-[oklch(0.24_0.03_85_/_0.4)] aria-pressed:bg-[oklch(0.25_0.08_85_/_0.3)] aria-pressed:[box-shadow:0_0_8px_oklch(0.5_0.12_85_/_0.2)]"
      aria-pressed={selected}
      aria-label={skill.name}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant={tierVariant} className="py-0 text-[10px]">
          {tierLabel}
        </Badge>
        {!skill.implemented && (
          <Badge
            variant="outline"
            className="border-border-heavy text-muted-foreground py-0 text-[10px]"
          >
            Not yet on the pitch
          </Badge>
        )}
      </div>
      <span className="text-sm leading-tight font-bold">{skill.name}</span>
      <p className="text-muted-foreground text-xs leading-relaxed">
        {skill.description}
      </p>
    </Button>
  )
}

interface StatOptionCardProps {
  imp: StatImprovementOption
  selected: boolean
  onClick: () => void
}

function StatOptionCard({ imp, selected, onClick }: StatOptionCardProps) {
  const isTargetNumber =
    imp.stat === "AG" || imp.stat === "PA" || imp.stat === "AV"
  const displayCurrent = isTargetNumber
    ? `${imp.current_value}+`
    : `${imp.current_value}`
  const displayNew = isTargetNumber ? `${imp.new_value}+` : `${imp.new_value}`

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      className="border-border bg-muted hover:border-accent aria-pressed:border-accent h-auto w-full cursor-pointer border-2 px-3.5 py-3 text-left whitespace-normal transition-all duration-150 hover:bg-[oklch(0.24_0.03_85_/_0.4)] aria-pressed:bg-[oklch(0.25_0.08_85_/_0.3)] aria-pressed:[box-shadow:0_0_8px_oklch(0.5_0.12_85_/_0.2)]"
      aria-pressed={selected}
    >
      <div className="flex w-full items-center justify-between gap-3">
        <span className="text-sm font-bold">{imp.name}</span>
        <span className="text-muted-foreground text-sm font-medium tabular-nums">
          {displayCurrent} → {displayNew}
        </span>
      </div>
    </Button>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export function LevelUpPanel({
  leagueId,
  teamId,
  playerId,
  playerName,
  advancementOptions,
  advancementsUsed,
  advancementsEarned,
}: LevelUpPanelProps) {
  const [step, setStep] = useState<Step>("dice")
  const [rollInput, setRollInput] = useState("")
  const [rollError, setRollError] = useState("")
  const [successText, setSuccessText] = useState("")
  const [remainingAfterSuccess, setRemainingAfterSuccess] = useState(0)

  const {
    data,
    setData,
    patch,
    processing,
    errors: serverErrors,
  } = useForm<{
    roll_result: number
    advancement: string
  }>({
    roll_result: 0,
    advancement: "",
  })

  const currentNumber = advancementsUsed + 1
  const totalEarned = advancementsEarned
  const showProgress = totalEarned > 1

  function handleInterpretRoll() {
    const val = parseInt(rollInput, 10)
    if (isNaN(val) || val < 2 || val > 12) {
      setRollError("Please enter a value between 2 and 12")
      return
    }
    setRollError("")
    setData({ roll_result: val, advancement: "" })
    setStep("select")
  }

  function handleConfirm() {
    // Snapshot remaining before submitting so the success step can tell the user
    // whether more level-ups are still pending after this one.
    const remaining = advancementsEarned - advancementsUsed - 1
    patch(levelUpLeagueTeamPlayerPath(leagueId, teamId, playerId), {
      onSuccess: () => {
        setSuccessText(data.advancement)
        setRemainingAfterSuccess(remaining)
        setStep("success")
      },
    })
  }

  function handleNextLevelUp() {
    setRollInput("")
    setRollError("")
    setSuccessText("")
    setRemainingAfterSuccess(0)
    setData({ roll_result: 0, advancement: "" })
    setStep("dice")
  }

  const rollResult = step !== "dice" ? data.roll_result : null
  const availableOptions =
    rollResult !== null
      ? getAvailableForRoll(rollResult, advancementOptions)
      : null

  const { primary_categories, secondary_categories, skills_by_category } =
    advancementOptions

  return (
    <div
      className="mb-6 animate-[levelup-glow_3s_ease-in-out_infinite] border-2 p-5"
      style={{
        borderColor: "oklch(0.55 0.15 85)",
        background: "oklch(0.18 0.04 85 / 0.3)",
        boxShadow:
          "0 0 16px oklch(0.5 0.12 85 / 0.15), inset 0 0 12px oklch(0.5 0.12 85 / 0.08)",
      }}
    >
      {/* ── Step 1: Dice Entry ─────────────────────────────────────────── */}
      {step === "dice" && (
        <>
          <div className="mb-4 flex items-center gap-3">
            <Star className="text-gold size-6 shrink-0" />
            <div>
              <h3 className="font-display text-gold text-xl leading-none font-bold">
                {showProgress
                  ? `Level Up ${currentNumber} of ${totalEarned}`
                  : "Level Up Available!"}
              </h3>
              <p className="text-muted-foreground mt-0.5 text-sm">
                {playerName} has earned enough SPP to advance. Roll the dice at
                your table and enter the result.
              </p>
            </div>
          </div>

          <div className="flex items-end gap-4">
            <div>
              <Label
                htmlFor="dice-roll-input"
                className="mb-1.5 block text-[10px] font-bold tracking-widest uppercase"
                style={{ color: "var(--accent-dim)" }}
              >
                2D6 Roll Result
              </Label>
              <Input
                id="dice-roll-input"
                type="number"
                min={2}
                max={12}
                value={rollInput}
                onChange={(e) => {
                  setRollInput(e.target.value)
                  setRollError("")
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleInterpretRoll()
                }}
                className="border-border-heavy bg-input font-display text-foreground focus:border-accent h-[52px] w-16 border-2 text-center text-[1.75rem] font-bold transition-[border-color] duration-150 outline-none focus:[box-shadow:0_0_8px_oklch(0.55_0.14_85_/_0.3)]"
                placeholder="—"
                aria-label="2D6 roll result"
                aria-invalid={rollError ? true : undefined}
                aria-describedby={rollError ? "roll-error" : undefined}
                autoFocus
              />
            </div>
            <Button
              type="button"
              variant="gold"
              onClick={handleInterpretRoll}
              className="h-[52px] px-6 text-lg"
            >
              Interpret Roll
            </Button>
          </div>

          {rollError && (
            <p
              id="roll-error"
              className="text-destructive mt-2 text-xs"
              role="alert"
            >
              {rollError}
            </p>
          )}

          <div className="text-muted-foreground mt-2 space-y-0.5 text-xs">
            <p>
              <span className="font-semibold">2–7:</span> Primary skill only
            </p>
            <p>
              <span className="font-semibold">8–9:</span> Primary or secondary
              skill
            </p>
            <p>
              <span className="font-semibold">10–11:</span> + stat improvement
            </p>
            <p>
              <span className="font-semibold">12:</span> Any skill or stat
            </p>
          </div>
        </>
      )}

      {/* ── Step 2: Option Selection ───────────────────────────────────── */}
      {step === "select" && availableOptions && rollResult !== null && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="font-display text-gold text-lg leading-none font-bold">
              Rolled: {rollResult}
            </span>
            <span
              className="px-2 py-0.5 text-xs font-bold tracking-wider uppercase"
              style={{
                background: "oklch(0.25 0.08 85 / 0.3)",
                color: "var(--accent)",
                border: "1px solid oklch(0.40 0.10 85)",
              }}
            >
              {labelForRoll(rollResult)}
            </span>
            {showProgress && (
              <span className="text-muted-foreground ml-auto text-xs font-semibold tracking-wide uppercase">
                Level Up {currentNumber} of {totalEarned}
              </span>
            )}
          </div>

          {serverErrors.advancement && (
            <p className="text-destructive mb-2 text-xs" role="alert">
              {serverErrors.advancement}
            </p>
          )}

          <div className="mb-4 max-h-[420px] space-y-5 overflow-y-auto pr-1">
            {availableOptions.unlockedCategories.map((cat) => {
              const skills = skills_by_category[cat] || []
              const isExhausted = skills.length === 0
              const isPrimaryCat = primary_categories.includes(cat)
              const isSecondaryCat = secondary_categories.includes(cat)

              return (
                <div key={cat}>
                  <div className="mb-2 flex items-baseline gap-2">
                    <p className="text-muted-foreground text-xs font-bold tracking-wide uppercase">
                      {CATEGORY_LABELS[cat] ?? cat}
                    </p>
                    {isExhausted && (
                      <span className="text-muted-foreground text-xs italic">
                        (all learned)
                      </span>
                    )}
                  </div>
                  {isExhausted ? (
                    <p className="text-muted-foreground border-border rounded border border-dashed py-3 text-center text-sm">
                      All skills in this category have been learned.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {skills.map((skill: SkillOption) => (
                        <SkillOptionCard
                          key={skill.name}
                          skill={skill}
                          selected={data.advancement === skill.name}
                          isPrimary={isPrimaryCat}
                          isSecondary={isSecondaryCat}
                          onClick={() => setData("advancement", skill.name)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}

            {availableOptions.includeStats && (
              <div>
                <p className="text-muted-foreground mb-2 text-xs font-bold tracking-wide uppercase">
                  Stat Improvements
                </p>
                {availableOptions.availableStats.length === 0 ? (
                  <p className="text-muted-foreground border-border rounded border border-dashed py-3 text-center text-sm">
                    No stat improvements available (max improvements reached).
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {availableOptions.availableStats.map(
                      (imp: StatImprovementOption) => (
                        <StatOptionCard
                          key={imp.name}
                          imp={imp}
                          selected={data.advancement === imp.name}
                          onClick={() => setData("advancement", imp.name)}
                        />
                      ),
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="gold"
              onClick={handleConfirm}
              disabled={!data.advancement || processing}
              className="h-10 px-5 text-base"
            >
              {processing ? "Applying…" : "Confirm Skill"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setStep("dice")
                setData("advancement", "")
              }}
              disabled={processing}
              className="h-10 gap-1 px-4 text-sm font-bold tracking-wider uppercase"
              style={{
                background: "none",
                border: "2px solid var(--border-heavy)",
                color: "var(--foreground)",
              }}
            >
              <ChevronLeft className="size-4" />
              Back
            </Button>
          </div>
        </>
      )}

      {/* ── Step 3: Success ───────────────────────────────────────────── */}
      {step === "success" && (
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <CheckCircle className="text-gold size-8" />
          <div>
            <h3 className="font-display text-gold text-xl leading-none font-bold">
              {successText.startsWith("+1")
                ? `${successText} — Stat Improved!`
                : `${successText} Learned!`}
            </h3>
            <p className="text-muted-foreground mt-1 text-sm">
              {remainingAfterSuccess > 0
                ? `${playerName} has ${remainingAfterSuccess} more level-up${remainingAfterSuccess === 1 ? "" : "s"} ready.`
                : successText.startsWith("+1")
                  ? `${playerName}'s stats have been updated.`
                  : `${playerName} has grown more powerful.`}
            </p>
          </div>
          {remainingAfterSuccess > 0 && (
            <Button
              type="button"
              variant="gold"
              onClick={handleNextLevelUp}
              className="mt-1 h-10 px-5 text-base"
            >
              Next Level-Up
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
